import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "../../lib/api";
import { Card, Btn, Empty, Modal } from "../../components/UI";
import { DateField } from "./DateField";
import { Plus, SquareArrowOutUpRight, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function ReferralList() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [page, setPage] = useState(1);
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const navigate = useNavigate();

  const load = () => {
    const params = { page, limit: 15, status: "unprocessed" };
    if (dateStart && dateEnd) { params.start = dateStart; params.end = dateEnd; }
    api.get("/referral-forms", { params }).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  };
  useEffect(() => { load(); }, [page, dateStart, dateEnd]);

  const doDelete = async () => {
    try {
      await api.delete(`/referral-forms/${confirmDelete.id}`);
      toast.success("Referral deleted");
      setConfirmDelete(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const fmt = (d) => { if (!d) return "-"; const [y, m, dd] = d.split("-"); return `${m}/${dd}/${y}`; };

  return (
    <div>
      <Card className="p-0">
        <div className="flex flex-wrap items-center justify-between gap-4 p-5">
          <h1 className="font-heading text-lg font-bold text-[#3E2723]">All Unprocessed Clients</h1>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-[#6D4C41]">by Date Range</span>
            <DateField testId="referral-date-start" placeholder="From" className="w-44" value={dateStart} disabledAfter={dateEnd || undefined}
              onChange={(v) => { if (dateEnd && v > dateEnd) { toast.error("Start date cannot be after the end date"); return; } setDateStart(v); setPage(1); }} />
            <DateField testId="referral-date-end" placeholder="To" className="w-44" value={dateEnd} disabledBefore={dateStart || undefined}
              onChange={(v) => { if (dateStart && v < dateStart) { toast.error("End date cannot be before the start date"); return; } setDateEnd(v); setPage(1); }} />
            <Btn data-testid="add-new-referral-btn" onClick={() => navigate("/engagement/referrals/new")} className="rounded-lg">
              <Plus size={16} /> Add New Referral
            </Btn>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead>
              <tr>{["Name", "Email", "Mobile no", "Date", "Account Number", "Social Security Number", "Service Requested", "Actions"].map((h) => (
                <th key={h} className="bg-[#EDE7DC] text-[#4E342E] font-semibold py-3 px-4">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {data.items.map((r) => {
                const d = r.demographics || {};
                const svc = (r.requested_services?.services || [])[0]?.service || "-";
                return (
                  <tr key={r.id} data-testid={`referral-row-${r.id}`} className="hover:bg-[#FDFCF8]">
                    <td className="border-b border-[#EFEBE0] py-3.5 px-4 font-medium text-[#3E2723]">{d.first_name} {d.last_name}</td>
                    <td className="border-b border-[#EFEBE0] py-3.5 px-4">{d.email || "-"}</td>
                    <td className="border-b border-[#EFEBE0] py-3.5 px-4">{d.cell_phone || d.home_phone || "-"}</td>
                    <td className="border-b border-[#EFEBE0] py-3.5 px-4">{fmt(r.date_referral_received)}</td>
                    <td className="border-b border-[#EFEBE0] py-3.5 px-4">{r.account_number}</td>
                    <td className="border-b border-[#EFEBE0] py-3.5 px-4">{d.ssn || "-"}</td>
                    <td className="border-b border-[#EFEBE0] py-3.5 px-4">{svc}</td>
                    <td className="border-b border-[#EFEBE0] py-3.5 px-4">
                      <div className="flex gap-2">
                        <button data-testid={`open-referral-${r.id}`} aria-label="Open" onClick={() => navigate(`/engagement/referrals/${r.id}/edit`)} className="h-9 w-9 rounded-lg bg-[#F5F2EB] text-[#6D4C41] flex items-center justify-center hover:bg-[#EDE7DC] transition-colors"><SquareArrowOutUpRight size={14} /></button>
                        <button data-testid={`edit-referral-${r.id}`} aria-label="Edit" onClick={() => navigate(`/engagement/referrals/${r.id}/edit`)} className="h-9 w-9 rounded-lg bg-[#F5F2EB] text-[#6D4C41] flex items-center justify-center hover:bg-[#EDE7DC] transition-colors"><Pencil size={14} /></button>
                        <button data-testid={`delete-referral-${r.id}`} aria-label="Delete" onClick={() => setConfirmDelete(r)} className="h-9 w-9 rounded-lg bg-[#F5F2EB] text-[#6D4C41] flex items-center justify-center hover:bg-[#FFEBEE] hover:text-[#C62828] transition-colors"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {data.items.length === 0 && <Empty text="No unprocessed referrals" />}
        </div>
        {data.pages > 1 && (
          <div className="flex items-center gap-2 p-5" data-testid="referral-pagination">
            <button data-testid="referral-prev-page" disabled={page <= 1} onClick={() => setPage(page - 1)} className="text-xs font-bold uppercase tracking-wide text-[#6D4C41] disabled:opacity-40 px-2">Previous</button>
            {Array.from({ length: data.pages }, (_, i) => i + 1).map((p) => (
              <button key={p} data-testid={`referral-page-${p}`} onClick={() => setPage(p)}
                className={`h-8 w-8 rounded-md text-sm font-semibold transition-colors ${p === page ? "bg-[#4E342E] text-white" : "text-[#6D4C41] hover:bg-[#F5F2EB]"}`}>{p}</button>
            ))}
            <button data-testid="referral-next-page" disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="text-xs font-bold uppercase tracking-wide text-[#6D4C41] disabled:opacity-40 px-2">Next</button>
          </div>
        )}
      </Card>

      <Modal open={!!confirmDelete} onClose={() => setConfirmDelete(null)} title="Delete Referral">
        <p className="text-sm text-[#6D4C41] mb-5">Delete the referral for <span className="font-semibold text-[#3E2723]">{confirmDelete?.demographics?.first_name} {confirmDelete?.demographics?.last_name}</span>? This cannot be undone.</p>
        <div className="flex justify-end gap-2">
          <Btn variant="ghost" onClick={() => setConfirmDelete(null)}>Cancel</Btn>
          <Btn data-testid="confirm-delete-referral-btn" variant="danger" onClick={doDelete}>Delete</Btn>
        </div>
      </Modal>
    </div>
  );
}
